import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router';

import { isPersistableQueryKey } from '@/config/persisted-query-params/constants';
import { readBag } from '@/config/persisted-query-params/readBag';
import { resolveStorageKey } from '@/config/persisted-query-params/resolveStorageKey';
import { useApp } from '@/contexts';

import buildRestorableParams from './buildRestorableParams';

const PersistedQueryParamsGate = () => {
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { selectedRegion, selectedCropSeason } = useApp();

  // Synchronous localStorage read in render is intentional (CSR-only app).
  // If SSR is ever added, this logic must move to a clientLoader.
  // Only treat the URL as authoritative when it already has cacheable
  // (persistable/prefixed) params, and skip restore in that case to avoid
  // mixing persisted state into an already-specified filter/pagination state.
  // Ephemeral params (OAuth code/state, po_id, etc.) should not block
  // restoration of persisted filters from the bag.
  const hasCacheableParams = [...searchParams.keys()].some(
    isPersistableQueryKey
  );

  if (hasCacheableParams) {
    return <Outlet />;
  }

  const storageKey = resolveStorageKey(pathname, {
    region: selectedRegion,
    cropSeason: selectedCropSeason
  });

  const snapshot = readBag()[storageKey];

  if (!snapshot || Object.keys(snapshot).length === 0) {
    return <Outlet />;
  }

  // Restore every persistable param (filters, pagination, columns, sort) to the
  // URL on navigation back — the URL is the single source of truth and each
  // table reads its state from there via useRouteQueryParams.
  // Filter actions clear their removed keys from the bag synchronously, so the
  // gate never sees a stale snapshot while the URL update is in flight. Other
  // actions clear the bag synchronously when removing the last cacheable param.
  const { next, hasRestorableParams } = buildRestorableParams(
    snapshot,
    searchParams
  );

  if (!hasRestorableParams) {
    return <Outlet />;
  }

  return <Navigate to={{ pathname, search: `?${next.toString()}` }} replace />;
};

export default PersistedQueryParamsGate;
